import { useCallback, useEffect, useState } from "react"
import { useUpdateEffect } from "react-use"
import { InputError } from "../styles/shared"
import { StyledContainerProps, StyledContainer, StyledInput, SearchIconWrap } from "./InputStyles"
import { CSSFn } from "../styles"
import { Flex } from "../Flex"
import { SearchIcon } from "../Icons"
import { useDebouncedState } from "../hooks"

type ClearInputControls = {
  setHasValue?: (hasText: boolean) => void
  setClearInput?: (shouldClear: boolean) => void
  clearInput?: boolean
}

type InputValidator = (value: string) => string | undefined

type InputOwnProps<T> = {
  validate?: InputValidator
  error?: string
  defaultValue?: string
  onChange?: (value: string) => void
  value?: string
  parse?: (value: string) => T
  css?: CSSFn
  children?: React.ReactNode
  dataTestId?: string
  searchButtonDataTestId?: string
  clearInputControls?: ClearInputControls
  debounceDelay?: number
}
export type InputProps<T> = InputOwnProps<T> &
  StyledContainerProps &
  Omit<React.ComponentPropsWithRef<typeof StyledInput>, "onChange" | "value">

// eslint-disable-next-line react/function-component-definition
function Input<T>({
  onChange,
  validate,
  parse,
  error,
  defaultValue,
  variant,
  css,
  onBlur,
  value,
  pattern,
  dataTestId,
  searchButtonDataTestId,
  clearInputControls,
  debounceDelay,
  ...inputProps
}: InputProps<T>) {
  const [controlledError, setControlledError] = useState<string | undefined>(undefined)
  const [liveValue, setLiveValue, debouncedState] = useDebouncedState<string>(
    value ?? defaultValue ?? ``,
    debounceDelay || 0
  )
  const [previousDebouncedState, setPreviousDebouncedState] = useState<string>(``)

  useEffect(() => {
    if (clearInputControls?.clearInput && clearInputControls?.setClearInput) {
      setLiveValue(``)
      clearInputControls?.setClearInput(false)
    }
  }, [clearInputControls, setLiveValue])

  useUpdateEffect(() => {
    setControlledError(error)
  }, [error])

  useEffect(() => {
    if (debounceDelay) {
      setLiveValue(value ?? defaultValue ?? ``)
    }
  }, [debounceDelay, setLiveValue, value, defaultValue])

  useUpdateEffect(() => {
    if (variant === `search`) return
    onChange?.(liveValue)
  }, [liveValue, onChange, variant])

  const handleSubmit = useCallback(() => {
    if (variant === `search`) {
      onChange?.(debounceDelay ? debouncedState : liveValue)
    }
  }, [debouncedState, debounceDelay, onChange, variant, liveValue])

  useEffect(() => {
    if (debouncedState !== previousDebouncedState) {
      handleSubmit()
      setPreviousDebouncedState(debouncedState)
    }
  }, [debouncedState, handleSubmit, previousDebouncedState])

  const onEnterPress = (e: React.KeyboardEvent) => {
    if (variant !== `search` || e.key !== `Enter`) return
    handleSubmit()
  }

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (validate) {
      setControlledError(validate(liveValue))
    }
    onBlur?.(event)
  }

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (clearInputControls?.setHasValue) clearInputControls.setHasValue(!!event.target.value.length)
    const regexp = new RegExp(pattern ?? `.*`)
    const changedValue = event.target.value
    if (changedValue !== undefined && (regexp.test(changedValue) || changedValue.length < 1)) {
      setLiveValue(changedValue)
    }
  }

  return (
    <>
      <Flex alignItems="center">
        <StyledContainer variant={variant} disabled={inputProps.disabled} css={css}>
          <StyledInput
            data-testid={dataTestId}
            value={liveValue}
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            onKeyDown={onEnterPress}
            {...inputProps}
          />
        </StyledContainer>
        {variant === `search` && (
          <SearchIconWrap onClick={handleSubmit} disabled={inputProps.disabled} data-testid={searchButtonDataTestId}>
            <SearchIcon variant="white" />
          </SearchIconWrap>
        )}
      </Flex>
      {controlledError && <InputError>{controlledError}</InputError>}
    </>
  )
}

Input.displayName = `Input`

Input.defaultProps = {
  validate: undefined,
  error: undefined,
  defaultValue: undefined,
  onChange: undefined,
  value: undefined,
  parse: undefined,
  css: undefined,
  children: undefined,
  dataTestId: undefined,
  searchButtonDataTestId: undefined,
  clearInputControls: undefined,
  debounceDelay: undefined,
}

export { Input }
